import { AppMarketPageIdentifier } from '../AppRoutes';

export const APP_MARKET_BASE_URL_KEY = 'appMarketBasePath';
const { location } = window;
/* split the hostname by first dot to get correct matching environment
   EX:
    www.clover.com - prod
    www.eu.clover.com - prod
    www.la.clover.com - prod
    sandbox.dev.clover.com - sandbox
    stg1.dev.clover.com - stg
    dev1.dev.clover.com - dev
    localhost:8080 - localhost
*/
export const getCloverHostname = (): string =>
  location.hostname.split('.')[0].replace(/[0-9]/g, '').replace('www', 'prod');

export const isLocalhost = (): boolean =>
  location.href.includes('localhost') || location.href.includes('ngrok');

export const isNetlifyHost = (): boolean => location.hostname.includes('netlify');

export const isProdEnvironment = (): boolean => /^(?:ss\d+|us|eu|la|www)/.test(location.hostname);

export const isCloverDevEnvironment = (hostname: string = location.hostname): boolean =>
  /^(dev|demo)\d+\.dev\.clover\.com$/.test(hostname) || isLocalhost() || isNetlifyHost();

/**
 * Returns true if the current server is a sandbox server as indicated by
 * having a "sandbox" in the url.
 *
 * Also supports an override. If 'isSandboxOverride' is set to true in the
 * sessionStorage, then it will return true.
 *
 * Command to run to simulate sandbox server:
 * sessionStorage.isSandboxOverride = true;
 *
 * Command to reset the previous override:
 * sessionStorage.isSandboxOverride = false;
 */
export const isSandboxEnvironment = (): boolean => {
  const sessionOverride: Nullable<string> = sessionStorage.getItem('isSandboxOverride');
  if (sessionOverride) {
    // sessionStorage converts bools to strings
    return JSON.parse(sessionOverride);
  }

  return location.hostname.includes('sandbox');
};

/**
 * gets the country from the URL
 * eg: extract eu from https://www.eu.clover.com/
 *
 * JS console overrides:
 * To simulate EU: sessionStorage.regionOverride = 'eu'
 * To simulate LA: sessionStorage.regionOverride = 'ar'
 */

type ClientRegionName = 'us' | 'eu' | 'la';
type ServerRegionName = 'US' | 'EU' | 'LATIN_AMERICA';

const regionMap: Record<ClientRegionName, ServerRegionName> = {
  us: 'US',
  eu: 'EU',
  la: 'LATIN_AMERICA',
};

export const getRegionBasedOnLocation = (hostnameOverride?: string): ClientRegionName => {
  if (sessionStorage.regionOverride) return sessionStorage.regionOverride.trim().toLowerCase();

  const hostname = hostnameOverride || document.location.hostname;

  if (hostname.includes('.eu.') || hostname.includes('stg3.dev')) {
    return 'eu';
  }

  if (hostname.includes('.la.') || hostname.includes('stg2.dev') || hostname.includes('dev7.dev')) {
    return 'la';
  }

  return 'us';
};

/**
 * Returns region name in the format used on the server side
 * @param hostnameOverride
 */
export const getServerRegionBasedOnLocation = (hostnameOverride?: string): ServerRegionName => {
  return regionMap[getRegionBasedOnLocation(hostnameOverride)];
};

export const isEuServer = (hostnameOverride?: string): boolean =>
  getRegionBasedOnLocation(hostnameOverride) === 'eu';
export const isLaServer = (hostnameOverride?: string): boolean =>
  getRegionBasedOnLocation(hostnameOverride) === 'la';
export const isUsServer = (hostnameOverride?: string): boolean =>
  getRegionBasedOnLocation(hostnameOverride) === 'us';

export const getBaseUrl = (): string => location.origin;

export type QueryParamValue = string | number | boolean | null | undefined;

export const parseQueryParams = (query?: string): Record<string, QueryParamValue> => {
  if (!query) return {};

  const keyValues = query.split('?')[1].split('&');
  return keyValues.reduce(
    (queryParams: Record<string, QueryParamValue>, keyValuePair: string) => {
      const [key, rawValue] = keyValuePair.split('=');
      queryParams[key] = rawValue;

      return queryParams;
    },
    {},
  );
};

export const setBaseAppUrl = (path: string): void => {
  sessionStorage.setItem(APP_MARKET_BASE_URL_KEY, path);
}

const getBaseAppUrl = (): string => {
  return sessionStorage.getItem(APP_MARKET_BASE_URL_KEY) || '';
}

/**
 * All AppMarket routes should be generated through this method
 * It adjusts the route based on the app mode, /appmarket for public app, /appmakert/m/<merchantId> for private app
 * @param routeKey this is page name
 * @param params array of prams to pass into the route, note the order of params is important | can be a string
 * @return string route
 */
export const generateRoute = (routeKey?: AppMarketPageIdentifier, params?: string[] | string): string => {
  let route =  getBaseAppUrl() + (routeKey ? `/${routeKey}` : ``);
  if (typeof params === 'string') route += `/${params}`
  else if (params) {
    for (let param of params){
      route += `/${param}`;
    }
  }
  return route;
};
